import React from 'react';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { C4DiagramCard } from '../C4DiagramCard/C4DiagramCard';
import { parseEntityRef } from '@backstage/catalog-model';
import { C4DiagramModel, toC4DiagramEntity } from '../C4DiagramCard/types';
// @ts-ignore
import * as uuid from 'uuid';

export const ContainerContextDiagramCard = () => {
  const { entity } = useEntity();
  const relatedSystems = useRelatedEntities(entity, { kind: 'System' });
  const relatedProviders = useRelatedEntities(entity, { kind: 'Provider' });
  const relatedGroups = useRelatedEntities(entity, { kind: 'Group' });
  const relatedComponents = useRelatedEntities(entity, { kind: 'Component' });

  if (
    !relatedSystems.loading &&
    !relatedGroups.loading &&
    !relatedProviders.loading &&
    !relatedComponents.loading &&
    entity?.metadata
  ) {
    const system = relatedSystems.entities?.find(
      it =>
        it.metadata.name === entity.spec!.system ||
        `${it.metadata.namespace}/${it.metadata.name}` ===
          entity.spec!.system ||
        `system:${it.metadata.namespace}/${it.metadata.name}` ===
          entity.spec!.system,
    )!;
    const model: C4DiagramModel = {
      type: 'context',
      context: toC4DiagramEntity(system, 'container', true),
      entities: [],
      relations: [],
    };
    model.context!.entities = [];
    model.context!.entities.push(toC4DiagramEntity(entity, undefined, true));
    const hasParts = entity.relations
      ?.filter(it => it.type === 'hasPart')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'component')
      .map(it => it.name);
    if (hasParts) {
      for (const component of hasParts) {
        const relatedEntity = relatedComponents.entities?.find(
          entity => entity.metadata.name === component,
        );
        if (relatedEntity) {
          model.context!.entities.push(
            toC4DiagramEntity(relatedEntity, 'context'),
          );
          model.relations.push({
            id: uuid.v4(),
            source: entity.metadata.name,
            target: relatedEntity.metadata.name,
            text: 'has part',
          });
        }
      }
    }
    const partOfComponents = entity.relations
      ?.filter(it => it.type === 'partOf')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'component')
      .map(it => it.name);
    if (partOfComponents) {
      for (const component of partOfComponents) {
        const relatedEntity = relatedComponents.entities?.find(
          entity => entity.metadata.name === component,
        );
        if (relatedEntity) {
          model.context!.entities.push(
            toC4DiagramEntity(relatedEntity, 'context'),
          );
          model.relations.push({
            id: uuid.v4(),
            source: relatedEntity.metadata.name,
            target: entity.metadata.name,
            text: 'has part',
          });
        }
      }
    }
    const usedByGroups = entity.relations
      ?.filter(it => it.type === 'usedBy')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'group')
      .map(it => it.name);
    if (usedByGroups) {
      for (const group of usedByGroups) {
        const relatedEntity = relatedGroups.entities?.find(
          entity => entity.metadata.name === group,
        );
        const relation = (entity.spec?.usedBy as any[])?.find(
          rel => rel.name === group,
        );
        if (relatedEntity) {
          model.entities.push(toC4DiagramEntity(relatedEntity));
          model.relations.push({
            id: uuid.v4(),
            source: relatedEntity.metadata.name,
            target: entity.metadata.name,
            text: relation?.metadata?.description,
          });
        }
      }
    }
    const dependsOnSystems = entity.relations
      ?.filter(it => it.type === 'dependsOn')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (dependsOnSystems) {
      for (const system of dependsOnSystems) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        const relationDependsOn = (
          entity.spec?.dependsOnSystems as any[]
        )?.find(rel => rel.name === system);
        const relationDependencyOf = (
          relatedEntity?.spec?.dependencyOfSystems as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationDependsOn?.metadata?.description
          ? relationDependsOn?.metadata?.description
          : relationDependencyOf?.metadata?.description;
        if (relatedEntity) {
          model.entities.push(toC4DiagramEntity(relatedEntity, 'context'));
          model.relations.push({
            id: uuid.v4(),
            source: entity.metadata.name,
            target: relatedEntity.metadata.name,
            text: description,
          });
        }
      }
    }
    const dependsOnComponents = entity.relations
      ?.filter(it => it.type === 'dependsOn')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'component')
      .map(it => it.name);
    if (dependsOnComponents) {
      for (const component of dependsOnComponents) {
        const relatedEntity = relatedComponents.entities?.find(
          entity => entity.metadata.name === component,
        );
        const relationDependsOn = (
          entity.spec?.dependsOnComponents as any[]
        )?.find(rel => rel.name === component);
        const relationDependencyOf = (
          relatedEntity?.spec?.dependencyOfComponents as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationDependsOn?.metadata?.description
          ? relationDependsOn?.metadata?.description
          : relationDependencyOf?.metadata?.description;
        if (relatedEntity) {
          model.context!.entities.push(
            toC4DiagramEntity(relatedEntity, 'context'),
          );
          model.relations.push({
            id: uuid.v4(),
            source: entity.metadata.name,
            target: relatedEntity.metadata.name,
            text: description,
          });
        }
      }
    }
    const dependencyOfSystems = entity.relations
      ?.filter(it => it.type === 'dependencyOf')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (dependencyOfSystems) {
      for (const system of dependencyOfSystems) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        const relationDependencyOf = (
          entity.spec?.dependencyOfSystems as any[]
        )?.find(rel => rel.name === system);
        const relationDependsOn = (
          relatedEntity?.spec?.dependsOnSystems as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationDependencyOf?.metadata?.description
          ? relationDependencyOf?.metadata?.description
          : relationDependsOn?.metadata?.description;
        if (relatedEntity) {
          model.entities.push(toC4DiagramEntity(relatedEntity, 'context'));
          model.relations.push({
            id: uuid.v4(),
            source: relatedEntity.metadata.name,
            target: entity.metadata.name,
            text: description,
          });
        }
      }
    }
    const dependencyOfComponents = entity.relations
      ?.filter(it => it.type === 'dependencyOf')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'component')
      .map(it => it.name);
    if (dependencyOfComponents) {
      for (const component of dependencyOfComponents) {
        const relatedEntity = relatedComponents.entities?.find(
          entity => entity.metadata.name === component,
        );
        const relationDependencyOf = (
          entity.spec?.dependencyOfComponents as any[]
        )?.find(rel => rel.name === component);
        const relationDependsOn = (
          relatedEntity?.spec?.dependsOnComponents as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationDependencyOf?.metadata?.description
          ? relationDependencyOf?.metadata?.description
          : relationDependsOn?.metadata?.description;
        if (relatedEntity) {
          model.context!.entities.push(
            toC4DiagramEntity(relatedEntity, 'context'),
          );
          model.relations.push({
            id: uuid.v4(),
            source: relatedEntity.metadata.name,
            target: entity.metadata.name,
            text: description,
          });
        }
      }
    }
    const consumesDataFrom = entity.relations
      ?.filter(it => it.type === 'consumesDataFrom')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'provider')
      .map(it => it.name);
    if (consumesDataFrom) {
      for (const provider of consumesDataFrom) {
        const relatedEntity = relatedProviders.entities?.find(
          entity => entity.metadata.name === provider,
        );
        const relationConsumesDataFrom = (
          entity.spec?.consumesDataFrom as any[]
        )?.find(rel => rel.name === provider);
        const relationProvidesDataTo = (
          relatedEntity?.spec?.providesDataTo as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationConsumesDataFrom?.metadata?.description
          ? relationConsumesDataFrom?.metadata?.description
          : relationProvidesDataTo?.metadata?.description;
        if (relatedEntity) {
          model.entities.push(toC4DiagramEntity(relatedEntity, 'context'));
          model.relations.push({
            id: uuid.v4(),
            source: entity.metadata.name,
            target: relatedEntity.metadata.name,
            text: description,
          });
        }
      }
    }
    const providesDataTo = entity.relations
      ?.filter(it => it.type === 'providesDataTo')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (providesDataTo) {
      for (const system of providesDataTo) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        const relationProvidesDataTo = (
          entity.spec?.providesDataTo as any[]
        )?.find(rel => rel.name === system);
        const relationConsumesDataFrom = (
          relatedEntity?.spec?.consumesDataFrom as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationProvidesDataTo?.metadata?.description
          ? relationProvidesDataTo?.metadata?.description
          : relationConsumesDataFrom?.metadata?.description;
        if (relatedEntity) {
          model.entities.push(toC4DiagramEntity(relatedEntity, 'context'));
          model.relations.push({
            id: uuid.v4(),
            source: relatedEntity.metadata.name,
            target: entity.metadata.name,
            text: description,
          });
        }
      }
    }
    return <C4DiagramCard model={model} />;
  }
  return <></>;
};
